import React, { useMemo, useState } from 'react';
import {
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	Popover,
	Typography,
} from '@mui/material';
import { FilterList, ArrowDropDown } from '@mui/icons-material';
import { SchedulerDepartment } from 'core/API/scheduler';
import {
	DepartmentFilterSelection,
	isAllDepartmentsSelected,
} from '../../utils/scheduleResourceUtils';
import { sortDepartmentsAlphabetically } from '../../utils/scheduleDepartmentUtils';
import {
	FULLSCREEN_OVERLAY_Z_INDEX,
	useFullscreenContainer,
} from 'core/utils/fullscreenContainer';
import './DepartmentFilter.scss';

interface DepartmentFilterProps {
	departments: SchedulerDepartment[];
	selectedDepartmentIds: DepartmentFilterSelection;
	onChange: (selection: DepartmentFilterSelection) => void;
}

const DepartmentFilter: React.FC<DepartmentFilterProps> = ({
	departments,
	selectedDepartmentIds,
	onChange,
}) => {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const portalContainer = useFullscreenContainer();
	const sortedDepartments = useMemo(
		() => sortDepartmentsAlphabetically(departments),
		[departments]
	);

	if (departments.length === 0) return null;

	const allSelected = isAllDepartmentsSelected(selectedDepartmentIds, departments);
	const selectedIds =
		selectedDepartmentIds === 'all' ? departments.map((d) => d.id) : selectedDepartmentIds;

	const selectedCount = allSelected ? departments.length : selectedIds.length;
	const selectionLabel =
		allSelected || selectedCount === departments.length
			? 'All'
			: selectedCount === 1
				? sortedDepartments.find((d) => selectedIds.includes(d.id))?.name || '1 Department'
				: `${selectedCount} Departments`;

	const handleToggleAll = () => {
		if (allSelected) {
			onChange([]);
			return;
		}
		onChange(departments.map((d) => d.id));
	};

	const handleToggleDepartment = (departmentId: number) => {
		const currentIds =
			selectedDepartmentIds === 'all' ? departments.map((d) => d.id) : [...selectedDepartmentIds];
		const isChecked = currentIds.includes(departmentId);
		const nextIds = isChecked
			? currentIds.filter((id) => id !== departmentId)
			: [...currentIds, departmentId];

		if (nextIds.length === 0) {
			onChange([]);
			return;
		}

		if (nextIds.length === departments.length) {
			onChange(departments.map((d) => d.id));
			return;
		}

		onChange(nextIds);
	};

	return (
		<Box className="department-filter">
			<Typography component="span" className="department-filter-label">
				Department
			</Typography>
			<Button
				variant="outlined"
				size="small"
				onClick={(event) => setAnchorEl(event.currentTarget)}
				className="department-filter-button"
				startIcon={<FilterList fontSize="small" />}
				endIcon={<ArrowDropDown fontSize="small" />}
				aria-haspopup="listbox"
				aria-expanded={Boolean(anchorEl)}
				aria-label={`Filter schedule by department. Currently showing: ${selectionLabel}`}
			>
				{selectionLabel}
			</Button>
			<Popover
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
				container={portalContainer}
				slotProps={{
					paper: { sx: { zIndex: FULLSCREEN_OVERLAY_Z_INDEX } },
				}}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}
			>
				<Box className="department-filter-menu" role="listbox" aria-label="Department filter options">
					<FormControlLabel
						className="department-filter-option"
						control={<Checkbox checked={allSelected} onChange={handleToggleAll} size="small" />}
						label="All"
					/>
					{sortedDepartments.map((department) => (
						<FormControlLabel
							key={department.id}
							className="department-filter-option"
							control={
								<Checkbox
									checked={allSelected || selectedIds.includes(department.id)}
									onChange={() => handleToggleDepartment(department.id)}
									size="small"
								/>
							}
							label={department.name}
						/>
					))}
				</Box>
			</Popover>
		</Box>
	);
};

export default DepartmentFilter;
